<template>
  <header class="top-header" :class="{ 'header-transparent': $route.meta.transparentHeader }">
    <!-- 頂部導航欄 -->
    <div class="top-nav">
      <div v-if="showNavActions" class="nav-left" :class="{ 'force-show': isDesktop && isFlashOptions }">
        <router-link to="/" class="brand-mark">
          <img src="/images/logo.png" alt="logo" class="logo-energy-img" />
          <div class="brand-copy">
            <div class="brand-line-1">
              <span class="brand-title">ecoWise Renewable Energy</span>
            </div>
            <div class="brand-line-2">
              <span class="brand-powered">新能源閃期權交易平臺</span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Desktop navigation links (only on flash-options where sidebar is hidden) -->
      <nav v-if="showNavActions && isDesktop && $route.path === '/flash-options'" class="desktop-nav">
        <router-link to="/" class="desktop-nav-link" :class="{ active: $route.path === '/' }">{{ $t('bottomNav.首页') }}</router-link>
        <router-link to="/flash-options" class="desktop-nav-link" :class="{ active: $route.path === '/flash-options' }">{{ $t('bottomNav.闪期权') }}</router-link>
        <router-link to="/assets" class="desktop-nav-link" :class="{ active: $route.path === '/assets' }">{{ $t('assets.我的账户') || $t('bottomNav.资产') }}</router-link>
      </nav>

      <div class="nav-spacer desktop-only"></div>

      <div v-if="showNavActions" class="nav-right">
        <!-- Customer Service -->
        <div class="nav-icon cs-icon" @click="openCustomerService">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span v-if="csUnreadCount > 0" class="cs-badge">{{ csUnreadCount > 99 ? '99+' : csUnreadCount }}</span>
        </div>

      </div>

      <!-- Flash Options history icon (mobile only) -->
      <div v-if="$route.meta.hideHeaderActions && !isDesktop" class="nav-right">
        <div class="nav-icon hist-icon" @click="toggleFlashHistory">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- 登錄中加載遮罩 -->
    <teleport to="body">
      <div v-if="isConnecting" class="wallet-loading-overlay">
        <div class="wallet-loading-modal">
          <div class="loading-spinner-large"></div>
          <h3 class="loading-title">登錄中...</h3>
          <p class="loading-subtitle">請稍候</p>
        </div>
      </div>
    </teleport>

    <!-- 登錄成功提示 -->
    <teleport to="body">
      <div v-if="showSuccessModal" class="wallet-success-overlay">
        <div class="wallet-success-modal">
          <div class="success-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#F0B90B" stroke-width="2"/>
              <path d="m9 12 2 2 4-4" stroke="#F0B90B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="success-title">登錄成功</h3>
          <p class="success-subtitle">歡迎回來</p>
        </div>
      </div>
    </teleport>

    <!-- 登錄/註冊彈窗 -->
    <teleport to="body">
      <div v-if="showLoginModal" class="wallet-modal-overlay" @click="showLoginModal = false">
        <div class="wallet-modal" @click.stop>
          <div class="wallet-modal-header">
            <h3>{{ authMode === 'login' ? '登錄' : '註冊' }}</h3>
            <button class="close-btn" @click="showLoginModal = false">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="wallet-modal-content">
            <div class="auth-tabs">
              <button class="auth-tab" :class="{ active: authMode === 'login' }" @click="authMode = 'login'">登錄</button>
              <button class="auth-tab" :class="{ active: authMode === 'register' }" @click="authMode = 'register'">註冊</button>
            </div>

            <div class="auth-form">
              <div class="form-field">
                <label>用戶名</label>
                <input v-model="authForm.username" type="text" placeholder="請輸入用戶名" autocomplete="username" @keyup.enter="submitAuth" />
              </div>
              <div class="form-field">
                <label>密碼</label>
                <input v-model="authForm.password" type="password" placeholder="請輸入密碼" autocomplete="current-password" @keyup.enter="submitAuth" />
              </div>
              <div v-if="authMode === 'register'" class="form-field">
                <label>確認密碼</label>
                <input v-model="authForm.confirmPassword" type="password" placeholder="請再次輸入密碼" @keyup.enter="submitAuth" />
              </div>
              <div v-if="authError" class="auth-error">{{ authError }}</div>
              <button class="auth-submit-btn" :disabled="isConnecting" @click="submitAuth">
                {{ isConnecting ? '處理中...' : (authMode === 'login' ? '登錄' : '註冊') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- 用戶信息彈窗 -->
    <teleport to="body">
      <div v-if="showWalletModal" class="wallet-modal-overlay" @click="closeWalletModal">
        <div class="wallet-modal" @click.stop>
          <div class="wallet-modal-header">
            <h3>賬戶信息</h3>
            <button class="close-btn" @click="closeWalletModal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="wallet-modal-content">
            <div class="wallet-overview-card">
              <div class="wallet-status">
                <div class="status-indicator connected"></div>
                <span class="status-text">已登錄</span>
              </div>
            </div>

            <div class="wallet-address-section">
              <label>用戶名</label>
              <div class="address-display" style="margin-top: 10px">
                <span class="full-address">{{ userInfo?.username || userInfo?.nickname || '--' }}</span>
              </div>
            </div>
          </div>

          <div class="wallet-modal-footer">
            <button class="disconnect-btn" @click="logoutUser">
              退出登錄
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- 全屏客服面板 -->
    <teleport to="body">
      <div v-if="csVisible" class="cs-fullscreen-overlay">
        <div class="cs-fullscreen-panel">
          <div class="cs-header">
            <div class="cs-header-left">
              <div class="cs-avatar"><img src="/images/logo.png" alt="logo" /></div>
              <div>
                <div class="cs-title">{{ $t('common.在线客服') }}</div>
                <div class="cs-status"><span class="cs-online-dot"></span>{{ $t('common.在线') }}</div>
              </div>
            </div>
            <button class="cs-close-btn" @click="closeCustomerService">✕</button>
          </div>
          <div ref="csMessageListRef" class="cs-body">
            <div v-for="msg in csMessages" :key="msg.id" class="cs-msg-row" :class="msg.role">
              <div class="cs-bubble" :class="{ 'cs-img': msg.type === 'image' }">
                <img v-if="msg.type === 'image'" :src="msg.imageUrl" alt="" class="cs-chat-img" />
                <span v-else>{{ msg.isWelcome ? $t('common.客服欢迎语') : msg.text }}</span>
              </div>
              <div class="cs-time">{{ formatCsTime(msg.time) }}</div>
            </div>
          </div>
          <div class="cs-footer">
            <input ref="csUploadRef" type="file" accept="image/*" class="cs-upload-input" @change="onCsUploadImage" />
            <button class="cs-upload-btn" @click="$refs.csUploadRef && $refs.csUploadRef.click()">+</button>
            <input
              v-model="csDraft"
              class="cs-msg-input"
              type="text"
              :placeholder="$t('common.输入消息占位')"
              @keyup.enter="sendCsMessage"
            />
            <button class="cs-send-btn" @click="sendCsMessage">{{ $t('common.发送') }}</button>
          </div>
        </div>
      </div>
    </teleport>
  </header>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { authApi, api, request } from '@/services/api'

export default {
  name: 'TopHeader',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      showLanguageDropdown: false,
      showWalletModal: false,
      showLoginModal: false,
      showSuccessModal: false,
      selectedLanguage: localStorage.getItem('selected_language') || localStorage.getItem('tenant_default_language') || 'zh-tw',
      isConnected: false,
      isConnecting: false,
      userInfo: null,
      walletInfo: null,
      authMode: 'login',
      authForm: { username: '', password: '', confirmPassword: '' },
      authError: '',
      allLanguages: [
        { code: 'fr', name: 'Français', flag: require('@/assets/flags/fr.svg') },
        { code: 'en', name: 'English', flag: require('@/assets/flags/eng.png') },
        { code: 'zh-tw', name: '中文（繁體）', flag: require('@/assets/flags/china.svg') },
        { code: 'de', name: 'Deutsch', flag: require('@/assets/flags/deu.svg') },
        { code: 'it', name: 'Italiano', flag: require('@/assets/flags/italy.svg') }
      ],
      enabledLanguageCodes: ['fr','en','zh-tw','de','it'],
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
      csVisible: false,
      csMessages: [],
      csDraft: '',
      csSending: false,
      csLastMessageId: null,
      csPollTimer: null,
      csUnreadCount: 0,
      csUnreadTimer: null
    }
  },
  computed: {
    isDesktop() {
      return this.windowWidth >= 769
    },
    isFlashOptions() {
      return this.$route.path === '/flash-options'
    },
    showNavActions() {
      if (!this.$route.meta.hideHeaderActions) return true
      return this.isDesktop
    },
    languages() {
      return this.allLanguages.filter(lang => this.enabledLanguageCodes.includes(lang.code))
    },
    displayUsername() {
      try {
        const info = JSON.parse(localStorage.getItem('user_info') || '{}')
        return info.username || ''
      } catch (_) { return '' }
    }
  },
  methods: {
    _onResize() {
      this.windowWidth = window.innerWidth
    },
    toggleFlashHistory() {
      window.dispatchEvent(new CustomEvent('toggle-flash-history'))
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown
    },
    selectLanguage(languageCode) {
      this.selectedLanguage = languageCode
      this.showLanguageDropdown = false
      localStorage.setItem('selected_language', languageCode)
      localStorage.setItem('user_chose_language', '1')
      if (this.$i18n) {
        this.$i18n.locale = languageCode
      }
      if (window.updateCurrentPageTitle) {
        window.updateCurrentPageTitle()
      }
    },

    syncLanguageSettings() {
      const savedLanguage = localStorage.getItem('selected_language')
      if (savedLanguage && savedLanguage !== this.selectedLanguage) {
        this.selectedLanguage = savedLanguage
        if (this.$i18n) {
          this.$i18n.locale = savedLanguage
        }
      }
    },

    async loadEnabledLanguages() {
      try {
        const data = await api.get('/system/config')
        if (data.success && data.data) {
          if (data.data.language) {
            const enabled = data.data.language.enabled_languages
            if (Array.isArray(enabled) && enabled.length > 0) {
              this.enabledLanguageCodes = enabled
            }
          }
          const sysDefaultLang = data.data.system?.defaultLanguage || data.data.system?.default_language
          if (sysDefaultLang) {
            localStorage.setItem('tenant_default_language', sysDefaultLang)
            const userChose = localStorage.getItem('user_chose_language') === '1'
            if (!userChose) {
              localStorage.setItem('selected_language', sysDefaultLang)
              this.selectedLanguage = sysDefaultLang
              if (this.$i18n) {
                this.$i18n.locale = sysDefaultLang
              }
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('加載語言設置失敗:', error)
        }
      }
    },

    handleUserClick() {
      if (this.isConnecting) return
      if (this.isConnected) {
        this.showWalletModal = true
      } else {
        this.authError = ''
        this.authForm = { username: '', password: '', confirmPassword: '' }
        this.showLoginModal = true
      }
    },

    closeWalletModal() {
      this.showWalletModal = false
    },

    async submitAuth() {
      this.authError = ''
      const { username, password, confirmPassword } = this.authForm

      if (!username || !password) {
        this.authError = '請輸入用戶名和密碼'
        return
      }

      if (this.authMode === 'register') {
        if (password.length < 6) {
          this.authError = '密碼至少6位'
          return
        }
        if (password !== confirmPassword) {
          this.authError = '兩次輸入的密碼不一致'
          return
        }
      }

      this.isConnecting = true
      try {
        let response
        if (this.authMode === 'login') {
          response = await authApi.login(username, password)
        } else {
          response = await authApi.register(username, password)
        }

        if (response.success) {
          this.isConnected = true
          this.userInfo = response.data.user
          this.walletInfo = response.data.wallet
          this.showLoginModal = false

          this.showSuccessModal = true
          setTimeout(() => { this.showSuccessModal = false }, 2000)

          this.$emit('user-connected', response.data)
          window.dispatchEvent(new CustomEvent('user-connected', { detail: response.data }))
        } else {
          this.authError = response.message || '操作失敗'
        }
      } catch (error) {
        this.authError = error.message || '網絡錯誤，請重試'
      } finally {
        this.isConnecting = false
      }
    },

    logoutUser() {
      authApi.logout()
      this.isConnected = false
      this.userInfo = null
      this.walletInfo = null
      this.showWalletModal = false
      window.location.reload()
    },

    checkLoginStatus() {
      const userInfo = localStorage.getItem('user_info')
      const walletToken = localStorage.getItem('wallet_token')

      if (userInfo && walletToken) {
        try {
          this.isConnected = true
          this.userInfo = JSON.parse(userInfo)
          const walletInfo = localStorage.getItem('wallet_info')
          if (walletInfo) this.walletInfo = JSON.parse(walletInfo)
        } catch {
          this.isConnected = false
        }
      }
    },

    handleLoginRequest() {
      if (!this.isConnected) {
        this.authError = ''
        this.authForm = { username: '', password: '', confirmPassword: '' }
        this.showLoginModal = true
      }
    },

    // ─── Customer Service ───
    async checkCsUnread() {
      if (this.csVisible) return
      const hasAuth = !!localStorage.getItem('wallet_token') || !!localStorage.getItem('cs_guest_token')
      if (!hasAuth) return
      try {
        const lastReadId = localStorage.getItem('cs_last_read_id') || '0'
        const res = await request(`/customer-service/unread-count?lastReadId=${lastReadId}`)
        if (res && res.success) this.csUnreadCount = res.data.count || 0
      } catch (_) { /* */ }
    },
    csMarkAsRead() {
      if (this.csLastMessageId) localStorage.setItem('cs_last_read_id', String(this.csLastMessageId))
      this.csUnreadCount = 0
    },
    async ensureCsGuestToken() {
      if (localStorage.getItem('wallet_token')) return true
      const existing = localStorage.getItem('cs_guest_token')
      const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || ''
      try {
        const headers = { 'Content-Type': 'application/json' }
        if (existing) headers.Authorization = `Bearer ${existing}`
        const resp = await fetch(`${API_BASE_URL}/api/c/customer-service/guest/init`, {
          method: 'POST',
          headers,
          body: '{}'
        })
        const data = await resp.json()
        if (data && data.success && data.data && data.data.token) {
          localStorage.setItem('cs_guest_token', data.data.token)
          return true
        }
      } catch (e) {
        console.error('初始化游客客服失败:', e)
      }
      return false
    },
    async openCustomerService() {
      if (!localStorage.getItem('wallet_token')) {
        const ok = await this.ensureCsGuestToken()
        if (!ok) return
      }
      this.csVisible = true
      await this.loadCsConversation()
      this.csMarkAsRead()
      this.csPollTimer = setInterval(() => this.pollCsMessages(), 3000)
    },
    closeCustomerService() {
      this.csMarkAsRead()
      this.csVisible = false
      if (this.csPollTimer) { clearInterval(this.csPollTimer); this.csPollTimer = null }
    },
    mapCsMessages(apiMessages) {
      return (apiMessages || []).map(msg => ({
        id: msg.id,
        role: msg.sender_type === 'user' ? 'user' : 'support',
        type: msg.type || 'text',
        text: msg.type === 'image' ? '' : msg.content,
        imageUrl: msg.type === 'image' ? msg.content : '',
        time: new Date(msg.created_at).getTime()
      }))
    },
    async loadCsConversation() {
      try {
        const res = await request('/customer-service/conversation')
        if (res.success) {
          const mapped = this.mapCsMessages(res.data.messages)
          const welcomeTime = mapped.length ? mapped[0].time - 1000 : Date.now()
          this.csMessages = [
            { id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: welcomeTime },
            ...mapped
          ]
          if (mapped.length) this.csLastMessageId = mapped[mapped.length - 1].id
          this.$nextTick(this.scrollCsBottom)
        }
      } catch (_) {
        this.csMessages = [{ id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: Date.now() }]
      }
    },
    async pollCsMessages() {
      if (!this.csVisible) return
      try {
        const res = await request('/customer-service/conversation')
        if (!res.success) return
        const mapped = this.mapCsMessages(res.data.messages)
        const lastKnown = this.csLastMessageId
        const newMsgs = lastKnown ? mapped.filter(m => m.id > lastKnown) : []
        if (newMsgs.length > 0) {
          for (const nm of newMsgs) {
            if (!this.csMessages.some(m => m.id === nm.id)) this.csMessages.push(nm)
          }
          this.csLastMessageId = mapped[mapped.length - 1].id
          this.csMarkAsRead()
          this.$nextTick(this.scrollCsBottom)
        }
      } catch (_) { /* */ }
    },
    async sendCsMessage() {
      const content = String(this.csDraft || '').trim()
      if (!content || this.csSending) return
      this.csSending = true
      const tempId = 'temp_' + Date.now()
      this.csMessages.push({ id: tempId, role: 'user', type: 'text', text: content, time: Date.now() })
      this.csDraft = ''
      this.$nextTick(this.scrollCsBottom)
      try {
        const res = await request('/customer-service/send', {
          method: 'POST',
          body: JSON.stringify({ content, type: 'text' })
        })
        if (res.success && res.data) {
          const idx = this.csMessages.findIndex(m => m.id === tempId)
          if (idx !== -1) {
            this.csMessages[idx].id = res.data.id
            this.csMessages[idx].time = new Date(res.data.created_at).getTime()
            this.csLastMessageId = res.data.id
          }
        }
      } catch (_) {
        const idx = this.csMessages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.csMessages.splice(idx, 1)
      }
      this.csSending = false
    },
    async onCsUploadImage(event) {
      const file = event.target?.files?.[0]
      if (!file) return
      if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) { event.target.value = ''; return }
      event.target.value = ''
      try {
        const formData = new FormData()
        formData.append('image', file)
        const uploadRes = await request('/customer-service/upload-image', { method: 'POST', body: formData })
        if (uploadRes.success) {
          const imageUrl = uploadRes.data.url
          const sendRes = await request('/customer-service/send', {
            method: 'POST',
            body: JSON.stringify({ content: imageUrl, type: 'image' })
          })
          if (sendRes.success && sendRes.data) {
            this.csMessages.push({ id: sendRes.data.id, role: 'user', type: 'image', imageUrl, time: new Date(sendRes.data.created_at).getTime() })
            this.csLastMessageId = sendRes.data.id
            this.$nextTick(this.scrollCsBottom)
          }
        }
      } catch (_) { /* */ }
    },
    scrollCsBottom() {
      const el = this.$refs.csMessageListRef
      if (el) el.scrollTop = el.scrollHeight
    },
    formatCsTime(ts) {
      const d = new Date(ts)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  },
  mounted() {
    this.checkLoginStatus()
    this.syncLanguageSettings()
    this.loadEnabledLanguages()

    window.addEventListener('start-wallet-authorization', this.handleLoginRequest)
    window.addEventListener('open-login-modal', this.handleLoginRequest)

    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showLanguageDropdown = false
      }
    })

    window.addEventListener('resize', this._onResize)

    this.checkCsUnread()
    this.csUnreadTimer = setInterval(() => this.checkCsUnread(), 10000)
  },

  beforeUnmount() {
    window.removeEventListener('start-wallet-authorization', this.handleLoginRequest)
    window.removeEventListener('open-login-modal', this.handleLoginRequest)
    window.removeEventListener('resize', this._onResize)
    if (this.csPollTimer) { clearInterval(this.csPollTimer); this.csPollTimer = null }
    if (this.csUnreadTimer) { clearInterval(this.csUnreadTimer); this.csUnreadTimer = null }
  }
}
</script>

<style scoped>
.top-header {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 1px solid #dce1e8;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
}
.top-header.header-transparent {
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(232, 238, 243, 0.5);
  box-shadow: none;
}

/* 頂部導航欄 */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
  min-height: 60px;
  background: transparent;
}

.nav-left .brand-mark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.logo-energy-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
}

.nav-left .brand-copy {
  display: grid;
  line-height: 1;
}

.brand-line-1 {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}

.brand-title {
  color: #1a2233;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.brand-miner {
  color: #2e7d32;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.brand-line-2 {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.brand-powered {
  color: #78909c;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.brand-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #2ff1d2;
  box-shadow: 0 0 0 3px rgba(47, 241, 210, 0.18);
}

.brand-status-text {
  color: #d7e4f1;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.nav-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.nav-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #dce1e8;
  background: #fff;
  box-shadow: none;
  padding: 6px;
}

.nav-icon:hover {
  border-color: #a5d6a7;
  background: #f1f8e9;
  transform: translateY(-1px);
}

.nav-icon.wallet-connected {
  border-color: rgba(47, 241, 210, 0.6);
  background: rgba(47, 241, 210, 0.2);
  box-shadow: 0 0 10px rgba(47, 241, 210, 0.3);
}

.nav-icon.wallet-connected > img {
  filter: brightness(0) saturate(100%) invert(80%) sepia(63%) saturate(564%) hue-rotate(103deg) brightness(101%) contrast(94%);
}

.nav-icon.wallet-connected:hover {
  border-color: rgba(47, 241, 210, 0.72);
  background: rgba(47, 241, 210, 0.24);
  box-shadow: 0 0 14px rgba(47, 241, 210, 0.36);
}

.nav-icon > img {
  width: 17px;
  height: 17px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(91%) sepia(10%) saturate(308%) hue-rotate(177deg) brightness(102%) contrast(95%);
  opacity: 1;
}

.nav-icon:hover > img {
  filter: brightness(0) saturate(100%) invert(80%) sepia(63%) saturate(564%) hue-rotate(103deg) brightness(101%) contrast(94%);
}

.hist-icon { color: rgba(200,220,240,0.8); }
.hist-icon:hover { color: #2ff1d2; }
.hist-icon svg { display: block; }

/* Desktop navigation - hidden on mobile */
.desktop-nav {
  display: none;
}

.brand-mark {
  text-decoration: none;
}

@media (min-width: 769px) {
  .top-header {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    box-shadow: none;
    background: var(--po-bg-dark, #131a24) !important;
  }

  .top-nav {
    padding: 0 24px;
    min-height: var(--po-header-h, 48px);
    gap: 12px;
  }

  .nav-left {
    display: none;
  }
  .nav-left.force-show {
    display: flex !important;
  }

  .nav-spacer.desktop-only { flex: 1; }

  .desktop-nav {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .desktop-nav-link {
    color: #546e7a;
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 6px;
    transition: all 0.15s ease;
    white-space: nowrap;
    border: none;
    background: none;
  }

  .desktop-nav-link:hover {
    color: #1a2233;
    background: #f1f8e9;
  }

  .desktop-nav-link.active {
    color: #2e7d32;
    background: #e8f5e9;
  }

  .nav-right {
    border: none;
    background: none;
    padding: 0;
    gap: 8px;
  }

  .nav-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid #dce1e8;
    background: #eaecf0;
    transition: background 0.15s ease;
  }

  .nav-icon:hover {
    background: #e8f5e9;
  }

  .nav-icon img {
    width: 18px;
    height: 18px;
    opacity: 0.7;
  }

  .nav-icon:hover img {
    opacity: 1;
  }
}


/* 語言和網絡選擇器需要相對定位 */
.language-selector,
.network-selector {
  position: relative;
}

/* 下拉菜單（與首頁 #10213b / 青綠主題一致） */
.language-dropdown,
.network-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #dce1e8;
  min-width: 240px;
  padding: 0 0 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  overflow: hidden;
}

.dropdown-header {
  padding: 14px 16px;
  color: #2e7d32;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #dce1e8;
  background: #f0f2f5;
}

.language-option,
.network-option {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 8px 0;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1px solid #dce1e8;
  background: #f5f6f8;
  color: #546e7a;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
  position: relative;
}

.language-dropdown .dropdown-header + .language-option,
.network-dropdown .dropdown-header + .network-option {
  margin-top: 10px;
}

.language-option:last-child,
.network-option:last-child {
  margin-bottom: 2px;
}

.language-option:hover:not(.active),
.network-option:hover:not(.active) {
  background: #f1f8e9;
  border-color: #c8e6c9;
  color: #1a2233;
}

.language-option.active,
.network-option.active {
  background: #e8f5e9;
  border-color: #a5d6a7;
  color: #1a2233;
}

.active-indicator {
  position: absolute;
  right: 12px;
  width: 8px;
  height: 8px;
  background: #2e7d32;
  border-radius: 50%;
}

/* 語言選項中的國旗圖標 */
.flag-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  filter: none !important;
}

/* 網絡選項中的圖標 */
.network-option-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  filter: none !important;
}

/* 錢包詳情彈窗樣式 */
.wallet-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(5, 8, 13, 0.76);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 14px;
  backdrop-filter: blur(4px);
}

.wallet-modal {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #dce1e8;
  width: 100%;
  max-width: 460px;
  max-height: min(84vh, 720px);
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.wallet-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 18px;
  border-bottom: 1px solid #dce1e8;
}

.wallet-modal-header h3 {
  color: #1a2233;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
}

.close-btn {
  width: 38px;
  height: 38px;
  background: #eaecf0;
  border: 1px solid #e0e7ed;
  color: #546e7a;
  color: #b8c2d6;
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #1a2233;
  background: #eceff1;
  border-color: #cfd8dc;
}

.wallet-modal-content {
  padding: 18px 24px 24px;
}

/* 連接狀態 */
.wallet-overview-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(240, 185, 11, 0.2);
  background: #f1f8e9;
  margin-bottom: 18px;
}

.wallet-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.connected {
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
}

.status-text {
  color: #2e7d32;
  font-weight: 700;
  font-size: 18px;
}

.wallet-network-chip {
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(240, 185, 11, 0.38);
  background: rgba(12, 15, 21, 0.35);
  color: #f4cf63;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 錢包地址部分 */
.wallet-address-section {
  margin-bottom: 0;
  border-radius: 14px;
  border: 1px solid #dce1e8;
  background: #f0f2f5;
  padding: 14px;
}

.wallet-address-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.wallet-address-section label {
  display: block;
  color: #546e7a;
  font-size: 14px;
  margin-bottom: 0;
  font-weight: 600;
}

.copy-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.08);
  color: #c6cedd;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn svg {
  width: 15px;
  height: 15px;
}

.copy-btn:hover {
  background: rgba(240, 185, 11, 0.14);
  border-color: rgba(240, 185, 11, 0.4);
  color: #f2cc5a;
}

.copy-btn.copied {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
  color: #22c55e;
}

.address-display {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 12px;
  padding: 12px;
}

.full-address {
  color: #1a2233;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  word-break: break-all;
  line-height: 1.45;
}

/* 彈窗底部 */
.wallet-modal-footer {
  padding: 16px 24px 22px;
  border-top: 1px solid #dce1e8;
}

.disconnect-btn {
  width: 100%;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  color: #e53935;
  height: 54px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.disconnect-btn:hover {
  background: #ffebee;
  border-color: #ef9a9a;
}

@media (max-width: 480px) {
  .nav-left .logo-emblem {
    width: 40px;
    height: 40px;
  }

  .brand-title,
  .brand-miner {
    font-size: 15px;
  }

  .brand-powered,
  .brand-status-text {
    font-size: 9px;
  }

  .wallet-modal-overlay {
    padding: 12px;
  }

  .wallet-modal {
    border-radius: 18px;
    max-height: 86vh;
  }

  .wallet-modal-header {
    padding: 18px 16px 14px;
  }

  .wallet-modal-header h3 {
    font-size: 22px;
  }

  .wallet-modal-content {
    padding: 14px 16px 18px;
  }

  .wallet-network-chip {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
  }

  .status-text {
    font-size: 16px;
  }

  .wallet-address-section label {
    font-size: 13px;
  }

  .full-address {
    font-size: 14px;
  }

  .wallet-modal-footer {
    padding: 12px 16px 16px;
  }

  .disconnect-btn {
    height: 48px;
    font-size: 16px;
  }
}

@media (max-width: 375px) {
  .wallet-modal-overlay {
    padding: 10px;
  }

  .wallet-modal-header h3 {
    font-size: 20px;
  }

  .status-text {
    font-size: 15px;
  }

  .full-address {
    font-size: 13px;
  }

  .disconnect-btn {
    height: 44px;
    border-radius: 12px;
    font-size: 15px;
  }
}

/* 錢包連接加載遮罩 */
.wallet-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.wallet-loading-modal {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #dce1e8;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  padding: 40px;
  text-align: center;
  max-width: 320px;
  width: 90%;
}

.loading-spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #dce1e8;
  border-top: 4px solid #2e7d32;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-title {
  color: #1a2233;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.loading-subtitle {
  color: #78909c;
  font-size: 14px;
  margin: 0;
}

/* 錢包連接成功遮罩 */
.wallet-success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease-out;
}

.wallet-success-modal {
  background: linear-gradient(180deg, rgba(33, 38, 47, 0.98), rgba(23, 27, 34, 0.98));
  border: 1px solid rgba(240, 185, 11, 0.26);
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  max-width: 320px;
  width: 90%;
  animation: slideUp 0.3s ease-out;
}

.success-icon {
  margin: 0 auto 20px;
  animation: scaleIn 0.5s ease-out 0.2s both;
}

.success-title {
  color: #f0b90b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.success-subtitle {
  color: #A0AEC0;
  font-size: 14px;
  margin: 0;
}

/* 動畫效果 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.auth-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #dce1e8;
}

.auth-tab {
  flex: 1;
  padding: 12px;
  background: #eaecf0;
  border: none;
  color: #78909c;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.auth-tab.active {
  background: #e8f5e9;
  color: #2e7d32;
}

.auth-tab:hover:not(.active) {
  background: #eceff1;
  color: #546e7a;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  color: #546e7a;
  font-size: 13px;
  font-weight: 600;
}

.form-field input {
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e0e7ed;
  background: #f0f2f5;
  color: #1a2233;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-field input:focus {
  border-color: #66bb6a;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.1);
}

.form-field input::placeholder {
  color: #b0bec5;
}

.auth-error {
  color: #e53935;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #ffebee;
  border: 1px solid #ffcdd2;
}

.auth-submit-btn {
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #43a047, #2e7d32);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
}

.auth-submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #388e3c, #1b5e20);
}

.auth-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ─── Customer Service Icon ─── */
.cs-icon {
  position: relative;
}
.cs-icon svg {
  color: #546e7a;
}
.cs-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
}

/* ─── Header Login / User Chip ─── */
.header-login-btn {
  height: 34px;
  padding: 0 16px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #43a047, #2e7d32);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.header-login-btn:hover {
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
  transform: translateY(-1px);
}

.header-user-chip {
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #dce1e8;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1a2233;
  transition: all 0.2s;
  white-space: nowrap;
  max-width: 120px;
}
.header-user-chip span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.header-user-chip svg {
  flex-shrink: 0;
  color: #2e7d32;
}
.header-user-chip:hover {
  border-color: #a5d6a7;
  background: #f1f8e9;
}

/* ─── Fullscreen Customer Service Panel ─── */
.cs-fullscreen-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
}
.cs-fullscreen-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  background: #fff;
  min-height: 0;
}
.cs-header {
  height: 56px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid #dce1e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.cs-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cs-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #e8f5e9;
}
.cs-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.cs-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a2233;
}
.cs-status {
  font-size: 12px;
  color: #78909c;
  display: flex;
  align-items: center;
  gap: 5px;
}
.cs-online-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #43a047;
}
.cs-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #dce1e8;
  background: #f0f2f5;
  color: #546e7a;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cs-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background: #f7f8fa;
  min-height: 0;
}
.cs-msg-row {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
}
.cs-msg-row.support { align-items: flex-start; }
.cs-msg-row.user { align-items: flex-end; }
.cs-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
}
.cs-bubble.cs-img { padding: 6px; }
.cs-chat-img {
  display: block;
  max-width: 200px;
  max-height: 200px;
  border-radius: 10px;
}
.cs-msg-row.support .cs-bubble {
  background: #fff;
  color: #1a2233;
  border: 1px solid #dce1e8;
}
.cs-msg-row.user .cs-bubble {
  background: linear-gradient(135deg, #43a047, #2e7d32);
  color: #fff;
}
.cs-time {
  margin-top: 4px;
  font-size: 11px;
  color: #90a4ae;
}
.cs-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  border-top: 1px solid #dce1e8;
  background: #fff;
  flex-shrink: 0;
}
.cs-upload-input { display: none; }
.cs-upload-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #dce1e8;
  background: #f0f2f5;
  color: #546e7a;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.cs-msg-input {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #dce1e8;
  background: #f7f8fa;
  color: #1a2233;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
}
.cs-msg-input::placeholder { color: #90a4ae; }
.cs-msg-input:focus { border-color: #a5d6a7; }
.cs-send-btn {
  height: 40px;
  min-width: 64px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #43a047, #2e7d32);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

@media (min-width: 769px) {
  .cs-fullscreen-panel {
    max-width: 480px;
    border-left: 1px solid #dce1e8;
    border-right: 1px solid #dce1e8;
  }
}

</style>
